import { Injectable } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { Store } from "@ngrx/store";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { from } from "rxjs";
import { exhaustMap, map, catchError, tap, mergeMap } from "rxjs/operators";

import { AppState } from "../../reducers";
import * as ProductGroupActionTypes from "../../actions/productGroups/productGroup.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";

import { ProductGroupService } from "src/app/shared/services/products/productGroup/productGroup.service";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";

import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { SnackBarUtils } from "src/app/shared/utils/snackbarUtils";
import { CategoriesService } from "src/app/shared/services/categories/categories.service";
import { PropertiesService } from "src/app/shared/services/properties/properties.service";

@Injectable()
export class ProductGroupEffects {
  constructor(
    private actions$: Actions,
    private productGroupService: ProductGroupService,
    private translate: TranslateService,
    private tableConfigService: TableConfigService,
    private propertiesService: PropertiesService,
    private categoriesService: CategoriesService,
    private store$: Store<AppState>
  ) {}

  getProductGroupList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductGroupActionTypes.getProductGroupList),
      exhaustMap((action) =>
        this.productGroupService
          .getProductGroupList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              ProductGroupActionTypes.loadProductGroupList({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                ProductGroupActionTypes.loadProductGroupList({
                  response: [],
                }),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("ProductGroup.loadError"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );
  geExceltProductGroupList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductGroupActionTypes.getExcelProductGroupList),
      exhaustMap((action) =>
        this.productGroupService
          .getProductGroupList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              ProductGroupActionTypes.loadExcelProductGroupList({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                ProductGroupActionTypes.loadExcelProductGroupList({
                  response: [],
                }),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("ProductGroup.loadExcelError"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Product Groups Table Config
  getProductGroupTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductGroupActionTypes.getProductGroupTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(action.options.code, action.options.settingType)
          .pipe(
            map((result) =>
              ProductGroupActionTypes.loadProductGroupTableConfig({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                ProductGroupActionTypes.loadProductGroupTableConfig({
                  response: [],
                }),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("TableConfig.LoadError"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Product Groups Table Config
  saveProductGroupTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductGroupActionTypes.saveProductGroupTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
            ProductGroupActionTypes.setLoadingEndState(),
            MessagesActionTypes.setSnackbar(
              SnackBarUtils.setSnackBar(
                this.translate.instant("TableConfig.SaveSuccess"),
                SnackbarType.SUCCESS
              )
            ),
          ]),
          catchError((error: any) => {
            const actions = [
              ProductGroupActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("TableConfig.SaveError"),
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getProductGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductGroupActionTypes.getProductGroup),
      exhaustMap((action) =>
        this.productGroupService.getProductGroupByCode(action.code).pipe(
          map((result) =>
            ProductGroupActionTypes.loadProductGroup({ response: result })
          ),
          catchError((error) => {
            const actions = [
              ProductGroupActionTypes.loadProductGroup({ response: [] }),
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("Products.loadProductError"),
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
  updateProductGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductGroupActionTypes.updateProductGroup),
      exhaustMap((action) =>
        this.productGroupService.updateProductGroup(action.productGroup).pipe(
          map((result) => {
            return ProductGroupActionTypes.getProductGroup({
              code: result?.result?.token,
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("ProductGroup.updateProductGroup"),
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant(
                    "ProductGroup.errorUpdateProductGroup"
                  ),
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
  getCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductGroupActionTypes.getCategoriesByUsage),
      exhaustMap((action) =>
        this.categoriesService
          .getCategoriesByUsage(action.pageReq, action.usageCode)
          .pipe(
            map((result) =>
              ProductGroupActionTypes.loadCategoriesByUsage({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                ProductGroupActionTypes.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("ProductGroup.ErrorLoadCategories"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );
  getProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductGroupActionTypes.getPropertiesByUsage),
      exhaustMap((action) =>
        this.propertiesService
          .getPropertiesByUsage(action.pageReq, action.usageCode)
          .pipe(
            map((result) =>
              ProductGroupActionTypes.loadPropertiesByUsage({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                ProductGroupActionTypes.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("ProductGroup.ErrorLoadProperties"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );
  createProductGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductGroupActionTypes.createProductGroup),
      exhaustMap((action) =>
        this.productGroupService.createProductGroup(action.productGroup).pipe(
          mergeMap((result) => [
            ProductGroupActionTypes.resetCreation(),
            ProductGroupActionTypes.getProductGroup({
              code: result?.result?.token,
            }),
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant("ProductGroup.createProductGroup"),
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  this.translate.instant(
                    error ? error : "ProductGroup.errorCreateProductGroup"
                  ),
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );
}
